/**
 * Pure view model for the redesigned Public Sentiment report (SPEC §3–§5).
 *
 * Every number the new blocks print is derived here from the frozen report —
 * the `psr-meta-v2` projection, the legacy meta keys (`assessment`,
 * `queryContext`, `sourceLeads`) and the frozen scan receipt
 * (`screeningBasis.sentimentScan`) — never from the live SED. React
 * components only lay these values out, so the truth rules are testable here:
 *
 *  - A report without a v2 projection has NO view: every block stays absent
 *    and the section renders exactly as before (attestation-neutral).
 *  - Local stance exists only in an `assessed` report with a finite index and
 *    at least one qualified local statement. Coverage-only and
 *    no-dated-local-stance reports never get an index, band, gauge or
 *    confidence, and a missing score is never 50.
 *  - Unscored records are counted in their own column and never folded into
 *    neutral.
 *  - All reader-facing template copy (hero sentence, status lines, caveats)
 *    lives here so it is audited in one place (`checkPublicSentimentAssertions`
 *    must pass on every template).
 */
import type { SiteRagReport } from '../../types/siteRag';
import type { PublicSentimentScope } from '../../types/siteEvidence';
import type { SentimentAssessment } from './publicSentimentScope';
import type { SentimentScreeningReceipt } from './publicSentimentScreening';
import {
  publicSentimentSourceCoverageStates,
  readPublicSentimentMetaV2,
  type PsrArchiveAudit,
  type PsrCoverageState,
  type PsrLedgerRow,
  type PsrReportState,
  type PsrScopeCount,
  type PsrSourceCoverageState,
  type PsrTechStance,
  type PublicSentimentMetaV2,
} from './publicSentimentMetaV2Contract';
import type { SentimentReasonCode } from './publicSentimentReasonCodes';
import { PSR_LOW_VOLUME_MONTH_MAX, PSR_TECH_LABELS } from './publicSentimentEvidenceTables';
import { hiddenReportSectionIds } from '../reportGeneration/sectionOmissions';

/* ── Labels ───────────────────────────────────────────────────────────── */

/** Reader names for source ids (SPEC artboards). Unknown ids print as-is. */
export const PSR_SOURCE_LABELS: Readonly<Record<string, string>> = Object.freeze({
  gdelt: 'News (GDELT)',
  'grounded-search': 'Grounded public-source research',
  'local-minutes': 'Local minutes',
  'federal-dockets': 'Federal / state dockets',
  'image-observation': 'Your uploaded photos',
  'uploaded-document': 'Uploaded consultation records',
  brandwatch: 'Brandwatch',
  talkwalker: 'Talkwalker',
  'census-acs': 'Census ACS',
  bls: 'BLS',
  fcc: 'FCC broadband',
  'local-public-data': 'Local open data',
  'local-gis': 'Local GIS',
  'news-aggregator': 'News aggregator',
  'tribal-records': 'Tribal records',
});

export const psrSourceLabel = (id: string): string => PSR_SOURCE_LABELS[id] ?? id;
export const psrTechLabel = (tech: string): string => (PSR_TECH_LABELS as Record<string, string>)[tech] ?? tech;

/** Chip / chart label for an unscored reason (full ledger rows, reasons chart). */
export const PSR_REASON_LABELS: Readonly<Record<SentimentReasonCode, string>> = Object.freeze({
  'policy-only': 'Agency or policy action',
  ambiguous: 'Mixed or unclear stance',
  undated: 'Source date unknown',
  'agenda-only': 'Agenda only, no testimony',
  'first-seen': 'Only a first-seen date',
  truncated: 'Passage truncated',
  'outside-speaker': 'Speaker from another locality',
  'out-of-window': 'Outside the observation window',
  'publisher-only': 'Local publisher only',
  'not-selected-technology': 'Not a selected technology',
  'no-usable-assessment': 'No usable stance assessment',
  'context-not-local': 'No dated local position',
});

/** Compact chip word for a context row in the ledger's compact table. */
export const PSR_REASON_SHORT_LABELS: Readonly<Record<SentimentReasonCode, string>> = Object.freeze({
  'policy-only': 'Policy action',
  ambiguous: 'Mixed / unclear',
  undated: 'Undated',
  'agenda-only': 'Agenda only',
  'first-seen': 'First-seen only',
  truncated: 'Truncated',
  'outside-speaker': 'Other locality',
  'out-of-window': 'Out of window',
  'publisher-only': 'Publisher only',
  'not-selected-technology': 'Other technology',
  'no-usable-assessment': 'No assessment',
  'context-not-local': 'No dated local position',
});

export const psrReasonLabel = (code: string): string => (PSR_REASON_LABELS as Record<string, string>)[code] ?? code;
export const psrReasonShortLabel = (code: string): string => (PSR_REASON_SHORT_LABELS as Record<string, string>)[code] ?? code;

export const PSR_SCOPE_LABELS: Readonly<Record<PublicSentimentScope, string>> = Object.freeze({
  site: 'Local (site / county)',
  state: 'Statewide context',
  country: 'National context',
});
export const PSR_SCOPE_SHORT_LABELS: Readonly<Record<PublicSentimentScope, string>> = Object.freeze({
  site: 'Local',
  state: 'Statewide',
  country: 'National',
});

export const PSR_COVERAGE_LABELS: Readonly<Record<PsrCoverageState, string>> = Object.freeze({
  full: 'Full',
  partial: 'Partial',
  none: 'None',
  failed: 'Failed',
  unreported: 'Unreported',
});

/** Legend definitions for the coverage glyphs (SPEC §1). */
export const PSR_COVERAGE_DEFINITIONS: Readonly<Record<PsrCoverageState, string>> = Object.freeze({
  full: 'reported complete',
  partial: 'partial; errors listed',
  none: 'attempted, nothing usable',
  failed: 'failed; error recorded with the scan',
  unreported: 'no coverage row, no recorded failure',
});

const DISCOVERY_EXCLUSION_LABELS: Readonly<Record<string, string>> = Object.freeze({
  'unrelated-to-selected-technology': 'Unrelated to a selected technology',
  'source-date-outside-window': 'Source date outside the window',
  'first-seen-outside-window': 'First-seen date outside the window',
  unspecified: 'Reason not recorded',
});
export const psrExclusionLabel = (reason: string): string => DISCOVERY_EXCLUSION_LABELS[reason] ?? reason;

const DATE_BASIS_LABELS: Readonly<Record<string, string>> = Object.freeze({
  published: 'Published',
  event: 'Event date',
  'scheduled-event': 'Scheduled meeting',
  'first-seen': 'First seen, not a source date',
});
export const psrDateBasisLabel = (basis: string | undefined): string | undefined => (basis ? DATE_BASIS_LABELS[basis] ?? basis : undefined);

const WORDS = ['no', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten'];
/** "three" for 3; digits above ten. */
export const psrCountWord = (value: number): string => (Number.isInteger(value) && value >= 0 && value <= 10 ? WORDS[value]! : String(value));
const plural = (value: number, one: string, many: string) => (value === 1 ? one : many);
const cap = (text: string) => (text ? text[0]!.toUpperCase() + text.slice(1) : text);

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function utcParts(iso: string | undefined): { y: number; m: number; d: number; hh: number; mm: number } | undefined {
  if (typeof iso !== 'string') return undefined;
  const at = Date.parse(iso);
  if (!Number.isFinite(at)) return undefined;
  const date = new Date(at);
  return { y: date.getUTCFullYear(), m: date.getUTCMonth(), d: date.getUTCDate(), hh: date.getUTCHours(), mm: date.getUTCMinutes() };
}

/** "18 Nov 2025" (UTC; a calendar day renders as that day everywhere). */
export function psrFormatDay(iso: string | undefined): string | undefined {
  const p = utcParts(iso);
  return p ? `${p.d} ${MONTHS[p.m]} ${p.y}` : undefined;
}
/** "14 Aug" */
export function psrFormatDayMonth(iso: string | undefined): string | undefined {
  const p = utcParts(iso);
  return p ? `${p.d} ${MONTHS[p.m]}` : undefined;
}
/** "17 Sep 2026, 13:58 UTC" */
export function psrFormatDateTime(iso: string | undefined): string | undefined {
  const p = utcParts(iso);
  return p ? `${p.d} ${MONTHS[p.m]} ${p.y}, ${String(p.hh).padStart(2, '0')}:${String(p.mm).padStart(2, '0')} UTC` : undefined;
}

/* ── The gate ─────────────────────────────────────────────────────────── */

const finite = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);

export interface PsrReportView {
  v2: PublicSentimentMetaV2;
  state: PsrReportState;
  /** Legacy meta `assessment`, only when its shape is intact. */
  assessment?: SentimentAssessment;
  techsQueried: string[];
  windowDays?: number;
  /** The frozen, hashed scan receipt the report was generated against. */
  scan?: SentimentScreeningReceipt;
  /** One display row per source (empty when the receipt is missing). */
  coverage: PsrSourceCoverageState[];
  /** Any source not Full, or the receipt missing ⇒ every chart carries the partial layer. */
  partial: boolean;
  /** The local support index — ONLY for an assessed report with qualified local stance. */
  index?: number;
  /** Site-scope counts (always present for assessed / no-dated-local-stance). */
  site?: PsrScopeCount;
  stateScope?: PsrScopeCount;
  country?: PsrScopeCount;
}

function readAssessment(value: unknown): SentimentAssessment | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const row = value as Record<string, unknown>;
  if (row.status !== 'assessed' && row.status !== 'insufficient-evidence') return undefined;
  const monthly = Array.isArray(row.monthly)
    ? row.monthly.filter((m): m is SentimentAssessment['monthly'][number] => !!m && typeof m === 'object'
      && typeof (m as Record<string, unknown>).month === 'string' && /^\d{4}-\d{2}$/.test((m as Record<string, unknown>).month as string)
      && finite((m as Record<string, unknown>).count) && finite((m as Record<string, unknown>).supportIndex))
    : [];
  return {
    method: 'dated-local-explicit-stance-v1',
    status: row.status,
    localObservationCount: finite(row.localObservationCount) ? row.localObservationCount : 0,
    unscoredObservationCount: finite(row.unscoredObservationCount) ? row.unscoredObservationCount : 0,
    ...(finite(row.supportIndex) ? { supportIndex: row.supportIndex } : {}),
    ...(finite(row.meanConfidence) ? { meanConfidence: row.meanConfidence } : {}),
    reason: typeof row.reason === 'string' ? row.reason : '',
    monthly,
  };
}

function readScan(value: unknown): SentimentScreeningReceipt | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const row = value as Record<string, unknown>;
  if (row.version !== 'sentiment-screening-v1' || typeof row.fetchedAt !== 'string' || !Array.isArray(row.sourceCoverage)) return undefined;
  return value as SentimentScreeningReceipt;
}

/**
 * The single gate for every new block: a Public Sentiment report carrying a
 * readable `psr-meta-v2` projection. `undefined` ⇒ legacy render, unchanged.
 */
export function psrReportView(report: Pick<SiteRagReport, 'reportType' | 'publicSentimentMeta' | 'screeningBasis'> | null | undefined): PsrReportView | undefined {
  if (!report || report.reportType !== 'public-sentiment') return undefined;
  const meta = report.publicSentimentMeta;
  const v2 = readPublicSentimentMetaV2(meta);
  if (!v2) return undefined;
  const legacy = (meta ?? {}) as Record<string, unknown>;
  const assessment = readAssessment(legacy.assessment);
  const queryContext = legacy.queryContext && typeof legacy.queryContext === 'object' ? legacy.queryContext as Record<string, unknown> : {};
  const techsQueried = Array.isArray(queryContext.techs) ? queryContext.techs.filter((t): t is string => typeof t === 'string') : [];
  const windowDays = finite(queryContext.windowDays) && queryContext.windowDays > 0 ? queryContext.windowDays : undefined;
  const scan = readScan(report.screeningBasis?.sentimentScan);
  const coverage = scan ? psrOrderCoverage(publicSentimentSourceCoverageStates(scan, v2)) : [];
  const scope = (name: PublicSentimentScope) => v2.scopeCounts?.find(row => row.scope === name);
  const site = scope('site');
  const stanced = site ? (site.supportive ?? 0) + (site.neutralStated ?? 0) + (site.opposed ?? 0) : 0;
  const index = v2.reportState === 'assessed' && assessment?.status === 'assessed' && finite(assessment.supportIndex)
    && stanced > 0 && assessment.supportIndex >= 0 && assessment.supportIndex <= 100
    ? assessment.supportIndex : undefined;
  // An assessed projection whose index did not survive (malformed or partial
  // persistence) cannot back a stance block, and "not established" would be
  // false: fall back to the legacy render, as the builder does on any
  // reconciliation failure.
  if (v2.reportState === 'assessed' && index === undefined) return undefined;
  return {
    v2,
    state: v2.reportState,
    ...(assessment ? { assessment } : {}),
    techsQueried,
    ...(windowDays ? { windowDays } : {}),
    ...(scan ? { scan } : {}),
    coverage,
    partial: !scan || coverage.some(row => row.coverage !== 'full'),
    ...(index !== undefined ? { index } : {}),
    ...(site ? { site } : {}),
    ...(scope('state') ? { stateScope: scope('state') } : {}),
    ...(scope('country') ? { country: scope('country') } : {}),
  };
}

/** Reading order for stance sources (SPEC artboards); others follow in attempt order. */
export const PSR_STANCE_SOURCE_ORDER: readonly string[] = Object.freeze(['local-minutes', 'gdelt', 'federal-dockets', 'image-observation']);
const COVERAGE_WORST_FIRST: Readonly<Record<PsrCoverageState, number>> = Object.freeze({ none: 0, failed: 1, unreported: 2, partial: 3, full: 4 });

/** Stable sort of stance source ids into the reading order. */
export function psrOrderStanceSources(ids: readonly string[]): string[] {
  const rank = (id: string) => { const at = PSR_STANCE_SOURCE_ORDER.indexOf(id); return at < 0 ? PSR_STANCE_SOURCE_ORDER.length : at; };
  return ids.map((id, i) => ({ id, i })).sort((a, b) => rank(a.id) - rank(b.id) || a.i - b.i).map(row => row.id);
}

/**
 * Display order: stance sources in reading order; context sources worst
 * coverage first (a gap is read before a completed source); other rows last.
 */
export function psrOrderCoverage(states: readonly PsrSourceCoverageState[]): PsrSourceCoverageState[] {
  const stanceOrder = psrOrderStanceSources(states.filter(row => row.role === 'stance').map(row => row.sourceId));
  const stance = stanceOrder.map(id => states.find(row => row.role === 'stance' && row.sourceId === id)!);
  const context = states.map((row, i) => ({ row, i })).filter(({ row }) => row.role === 'context')
    .sort((a, b) => COVERAGE_WORST_FIRST[a.row.coverage] - COVERAGE_WORST_FIRST[b.row.coverage] || a.i - b.i).map(({ row }) => row);
  return [...stance, ...context, ...states.filter(row => row.role === 'requested')];
}

/** Band words and thresholds used by the report service (≤35 / 36–64 / ≥65). */
export type PsrBand = 'opposed' | 'mixed' | 'supportive';
export function psrBand(index: number): PsrBand {
  return index >= 65 ? 'supportive' : index <= 35 ? 'opposed' : 'mixed';
}
export const PSR_BAND_LABELS: Readonly<Record<PsrBand, { word: string; range: string }>> = Object.freeze({
  opposed: { word: 'Opposed', range: 'band 0–35' },
  mixed: { word: 'Mixed', range: 'band 36–64' },
  supportive: { word: 'Supportive', range: 'band 65–100' },
});

export const psrStancedCount = (row: Pick<PsrScopeCount, 'supportive' | 'neutralStated' | 'opposed'> | undefined): number =>
  row ? (row.supportive ?? 0) + (row.neutralStated ?? 0) + (row.opposed ?? 0) : 0;

/* ── Coverage ─────────────────────────────────────────────────────────── */

export interface PsrCoverageTally { total: number; full: number; partial: number; none: number; failed: number; unreported: number }

export function psrCoverageTally(states: readonly PsrSourceCoverageState[], role: PsrSourceCoverageState['role']): PsrCoverageTally {
  const rows = states.filter(row => row.role === role);
  const count = (state: PsrCoverageState) => rows.filter(row => row.coverage === state).length;
  return { total: rows.length, full: count('full'), partial: count('partial'), none: count('none'), failed: count('failed'), unreported: count('unreported') };
}

/** "0 of 4 Full · 1 partial · 3 unreported" (zero states omitted). */
export function psrCoverageTallyText(tally: PsrCoverageTally): string {
  const parts = [`${tally.full} of ${tally.total} Full`];
  for (const state of ['partial', 'failed', 'none', 'unreported'] as const) {
    if (tally[state] > 0) parts.push(`${tally[state]} ${state}`);
  }
  return parts.join(' · ');
}
/** The detail line under a KPI: "1 partial · 3 unreported", or "all complete". */
export function psrCoverageShortfallText(tally: PsrCoverageTally): string {
  const parts: string[] = [];
  for (const state of ['partial', 'failed', 'none', 'unreported'] as const) {
    if (tally[state] > 0) parts.push(`${tally[state]} ${state}`);
  }
  return parts.length ? parts.join(' · ') : 'all complete';
}

const joinNames = (names: string[]) => (names.length <= 1 ? names.join('') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`);

/**
 * One sentence on the stance sources that are not Full, e.g.
 * "Local minutes is partial; three stance sources are unreported."
 * Empty when every stance source is Full (context-only gaps are named by
 * the coverage disclosure, not repeated on every chart).
 */
export function psrStanceCoverageSentence(states: readonly PsrSourceCoverageState[]): string {
  const stance = states.filter(row => row.role === 'stance');
  const names = (state: PsrCoverageState) => stance.filter(row => row.coverage === state).map(row => psrSourceLabel(row.sourceId));
  const clauses: string[] = [];
  const partial = names('partial');
  if (partial.length) clauses.push(`${joinNames(partial)} ${plural(partial.length, 'is', 'are')} partial`);
  const failed = names('failed');
  if (failed.length) clauses.push(`${joinNames(failed)} failed`);
  const none = names('none');
  if (none.length) clauses.push(`${joinNames(none)} returned nothing usable`);
  const unreported = stance.filter(row => row.coverage === 'unreported').length;
  if (unreported) clauses.push(`${psrCountWord(unreported)} stance ${plural(unreported, 'source is', 'sources are')} unreported`);
  return clauses.length ? `${cap(clauses.join('; '))}.` : '';
}

/** Sentence used when the report carries no frozen scan receipt. */
export const PSR_COVERAGE_UNRECORDED = 'Source coverage for this scan is not recorded in the report, so completeness cannot be shown.';

/** The shared partial-layer caveat, with a block-specific tail. */
export function psrPartialCaveat(view: Pick<PsrReportView, 'coverage' | 'scan'>, tail: string): string {
  if (!view.scan) return `${PSR_COVERAGE_UNRECORDED} ${tail}`.trim();
  const head = psrStanceCoverageSentence(view.coverage);
  if (head) return `${head} ${tail}`.trim();
  const context = psrCoverageTally(view.coverage, 'context');
  const gaps = context.total - context.full;
  return `Every stance source reported Full; ${psrCountWord(gaps)} context ${plural(gaps, 'source did', 'sources did')} not. ${tail}`.trim();
}

/* ── Executive ────────────────────────────────────────────────────────── */

/**
 * The hero sentence (template, never model prose). Built only from the
 * site-scope split; unscored records are named as unscored, never neutral.
 */
export function psrHeroSentence(site: PsrScopeCount): string {
  const stanced = psrStancedCount(site);
  const head = `Of ${site.total} local ${plural(site.total, 'record', 'records')}, ${stanced} carried a dated, explicit stance: `
    + `${site.supportive ?? 0} supportive, ${site.neutralStated ?? 0} neutral, ${site.opposed ?? 0} opposed.`;
  return site.unscored > 0 ? `${head} ${site.unscored} could not be scored.` : head;
}
export const PSR_NOT_A_SURVEY = 'Not a survey of residents.';

export const PSR_STATUS_NOT_ESTABLISHED = 'Local stance: not established';
export const PSR_STATUS_UNKNOWN = 'Community stance and confidence: unknown';
export const PSR_STATUS_UNKNOWN_LINE = 'Completed coverage-only screening; community stance and confidence are unknown.';
export const PSR_DEFAULT_NOT_ESTABLISHED_REASON = 'No dated, attributable local stance for the selected technologies was established. Regional context and missing coverage do not establish neutrality.';
export const PSR_EMPTY_IS_NOT_NEUTRAL = 'No index, band or confidence is shown. A missing score is not a 50/100 midpoint, and statewide or national records cannot stand in for local stance.';
export const PSR_DISCLOSURE_FOOTNOTE = 'Counts in this report describe what was read, not everything that exists.';

/** One KPI-rail cell (rendered by the UI rail and the office exports alike). */
export interface PsrKpiItem { key: string; value: string; unit?: string; note?: string }

/**
 * The executive KPI rail (SPEC §3.01 / §4). An index, confidence or change
 * appears ONLY with assessed local stance; the no-stance products carry
 * record and coverage counts, never a stance number.
 */
export function psrKpiItems(view: PsrReportView, entryCount?: number): PsrKpiItem[] {
  const items: PsrKpiItem[] = [];
  const stance = psrCoverageTally(view.coverage, 'stance');
  const context = psrCoverageTally(view.coverage, 'context');
  const hasReceipt = !!view.scan;
  if (view.state === 'coverage-only') {
    const stanceN = view.v2.sourceRoles.stanceSources.length;
    const contextN = view.v2.sourceRoles.contextSources.filter(id => !view.v2.sourceRoles.stanceSources.includes(id)).length;
    items.push({ key: 'Sources attempted', value: String(stanceN + contextN), note: `${stanceN} stance · ${contextN} context` });
    if (hasReceipt) {
      items.push({ key: 'Stance sources full', value: String(stance.full), unit: `of ${stance.total}`, note: psrCoverageShortfallText(stance) });
      items.push({ key: 'Context sources full', value: String(context.full), unit: `of ${context.total}`, note: psrCoverageShortfallText(context) });
    }
    if (typeof entryCount === 'number' && Number.isFinite(entryCount)) {
      items.push({ key: 'Records', value: String(entryCount), note: entryCount === 0 ? 'no local or context rows' : 'none assessed' });
    }
  } else if (view.index !== undefined && view.site) {
    const contextTotal = (view.stateScope?.total ?? 0) + (view.country?.total ?? 0);
    const contextUnscored = (view.stateScope?.unscored ?? 0) + (view.country?.unscored ?? 0);
    items.push({ key: 'Assessed local', value: String(psrStancedCount(view.site)), unit: 'statements', note: 'dated, explicit, site scope' });
    items.push({ key: 'Local unscored', value: String(view.site.unscored), unit: 'records', note: 'reasons under Research Detail' });
    items.push({ key: 'Context unscored', value: String(contextUnscored), unit: 'records', note: `of ${contextTotal} state and national` });
    const meanConfidence = view.assessment?.meanConfidence;
    if (typeof meanConfidence === 'number' && Number.isFinite(meanConfidence) && meanConfidence > 0) {
      items.push({ key: 'Mean confidence', value: meanConfidence.toFixed(2), note: 'assessed local only' });
    }
    const prior = view.v2.priorScan;
    if (prior?.sameQuery && prior.supportIndexDelta !== undefined && prior.supportIndex !== undefined) {
      const delta = prior.supportIndexDelta;
      items.push({ key: `Since ${psrFormatDayMonth(prior.fetchedAt) ?? 'prior scan'}`, value: `${delta > 0 ? '+' : delta < 0 ? '−' : '±'}${Math.abs(delta)}`, unit: 'index pts', note: 'comparable scan' });
    }
  } else if (view.site && view.state === 'no-dated-local-stance') {
    items.push({ key: 'Local records', value: String(view.site.total), note: view.site.total === 0 ? 'none kept' : 'all unscored' });
    items.push({ key: 'Context records', value: String((view.stateScope?.total ?? 0) + (view.country?.total ?? 0)), note: `state ${view.stateScope?.total ?? 0} · national ${view.country?.total ?? 0}` });
    if (hasReceipt) {
      items.push({ key: 'Stance sources full', value: String(stance.full), unit: `of ${stance.total}`, note: psrCoverageShortfallText(stance) });
      items.push({ key: 'Context sources full', value: String(context.full), unit: `of ${context.total}`, note: psrCoverageShortfallText(context) });
    }
    const prior = view.v2.priorScan;
    items.push(prior
      ? { key: 'Prior scan', value: psrFormatDay(prior.fetchedAt) ?? 'Recorded', note: 'no change shown without a local stance' }
      : { key: 'Prior scan', value: 'None', note: 'first scan · no change shown' });
  }
  return items;
}

/** Reason chips for the status block: local unscored records by reason. */
export function psrLocalReasonChips(v2: PublicSentimentMetaV2): Array<{ reasonCode: string; label: string; count: number }> {
  return (v2.unscoredByReason ?? []).filter(row => row.local > 0)
    .map(row => ({ reasonCode: row.reasonCode, label: psrReasonLabel(row.reasonCode), count: row.local }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
}

/* ── Per-technology ───────────────────────────────────────────────────── */

export interface PsrTechRow extends PsrTechStance { label: string; stanced: number }
export function psrTechRows(v2: PublicSentimentMetaV2): PsrTechRow[] {
  return (v2.stanceByTech ?? []).map(row => ({ ...row, label: psrTechLabel(row.btmTech), stanced: row.supportive + row.neutralStated + row.opposed }));
}

/** Selectable technologies that were not queried for this site. */
const TECH_READING_ORDER = ['general-datacenter', 'natural-gas', 'solar-bess', 'nuclear-smr', 'wind-bess'];
export function psrTechsNotQueried(techsQueried: readonly string[]): string[] {
  return TECH_READING_ORDER.filter(tech => !techsQueried.includes(tech)).map(psrTechLabel);
}

export interface PsrHeatCell { assessedLocal: number; siteRows: number }
export interface PsrHeatmap {
  techs: string[];
  sources: string[];
  cell: (tech: string, source: string) => PsrHeatCell | undefined;
  rowTotal: (tech: string) => PsrHeatCell;
  colTotal: (source: string) => PsrHeatCell;
  total: PsrHeatCell;
}

/** Technology × stance-source counts (site scope): assessed local of local rows. */
export function psrHeatmap(v2: PublicSentimentMetaV2): PsrHeatmap {
  const rows = v2.countsByTechSource ?? [];
  const techs = v2.stanceByTech?.length ? v2.stanceByTech.map(row => row.btmTech as string)
    : [...new Set(rows.filter(row => row.siteRows > 0).map(row => row.btmTech as string))];
  const sources = [...psrOrderStanceSources(v2.sourceRoles.stanceSources),
    ...[...new Set(rows.filter(row => row.siteRows > 0).map(row => row.sourceId))].filter(id => !v2.sourceRoles.stanceSources.includes(id))];
  const sum = (list: typeof rows): PsrHeatCell => ({
    assessedLocal: list.reduce((n, row) => n + row.assessedLocal, 0),
    siteRows: list.reduce((n, row) => n + row.siteRows, 0),
  });
  const inTechs = rows.filter(row => techs.includes(row.btmTech));
  return {
    techs,
    sources,
    cell: (tech, source) => {
      const hit = rows.find(row => row.btmTech === tech && row.sourceId === source);
      return hit && hit.siteRows > 0 ? { assessedLocal: hit.assessedLocal, siteRows: hit.siteRows } : undefined;
    },
    rowTotal: tech => sum(rows.filter(row => row.btmTech === tech && sources.includes(row.sourceId))),
    colTotal: source => sum(inTechs.filter(row => row.sourceId === source)),
    total: sum(inTechs.filter(row => sources.includes(row.sourceId))),
  };
}

/** Heatmap bin: 0 / 1 / 2 / 3+ (counts, never means). */
export function psrHeatBin(assessedLocal: number): 0 | 1 | 2 | 3 {
  if (assessedLocal <= 0) return 0;
  return assessedLocal >= 3 ? 3 : (assessedLocal as 1 | 2);
}

/* ── Trend ────────────────────────────────────────────────────────────── */

export interface PsrTrendMonth {
  /** YYYY-MM */
  month: string;
  label: string;
  /** Set on the first month and on every January. */
  year?: string;
  count: number;
  index?: number;
  lowVolume: boolean;
}

const monthKey = (y: number, m: number) => `${y}-${String(m + 1).padStart(2, '0')}`;

/**
 * Every calendar month of the observation window (scan date back
 * `windowDays`), with the stored monthly counts. A month with no dated local
 * statement is count 0 and has NO index (a gap, never 50).
 */
export function psrTrendMonths(monthly: SentimentAssessment['monthly'], scanAt: string | undefined, windowDays: number | undefined): PsrTrendMonth[] {
  const byMonth = new Map(monthly.filter(row => finite(row.count) && row.count > 0).map(row => [row.month, row]));
  const end = utcParts(scanAt);
  const keys = [...byMonth.keys()].sort();
  let startY: number; let startM: number; let endY: number; let endM: number;
  if (end && windowDays) {
    const start = utcParts(new Date(Date.parse(scanAt!) - windowDays * 86_400_000).toISOString())!;
    startY = start.y; startM = start.m; endY = end.y; endM = end.m;
  } else if (keys.length) {
    [startY, startM] = [Number(keys[0]!.slice(0, 4)), Number(keys[0]!.slice(5)) - 1];
    [endY, endM] = [Number(keys[keys.length - 1]!.slice(0, 4)), Number(keys[keys.length - 1]!.slice(5)) - 1];
  } else return [];
  // Months recorded outside the computed window still show (never dropped).
  if (keys.length) {
    const first = keys[0]!; const last = keys[keys.length - 1]!;
    if (first < monthKey(startY, startM)) [startY, startM] = [Number(first.slice(0, 4)), Number(first.slice(5)) - 1];
    if (last > monthKey(endY, endM)) [endY, endM] = [Number(last.slice(0, 4)), Number(last.slice(5)) - 1];
  }
  const out: PsrTrendMonth[] = [];
  for (let y = startY, m = startM; (y < endY || (y === endY && m <= endM)) && out.length < 60; m === 11 ? (y += 1, m = 0) : (m += 1)) {
    const key = monthKey(y, m);
    const row = byMonth.get(key);
    out.push({
      month: key,
      label: MONTHS[m]!,
      ...(out.length === 0 || m === 0 ? { year: String(y) } : {}),
      count: row?.count ?? 0,
      ...(row ? { index: row.supportIndex } : {}),
      lowVolume: !row || row.count <= PSR_LOW_VOLUME_MONTH_MAX,
    });
  }
  return out;
}

/** With this many months carrying a point or fewer, the trend is dots only (Components, trend edge case). */
export const PSR_TREND_DOTS_ONLY_MAX_MONTHS = 2;

/**
 * Runs of ≥2 CONSECUTIVE months that each rest on ≥4 statements (the only
 * joined lines). None when two or fewer months carry a point at all: a line
 * between two points reads as a trend the data cannot support.
 */
export function psrTrendJoinedRuns(months: readonly PsrTrendMonth[]): Array<[number, number]> {
  const runs: Array<[number, number]> = [];
  if (months.filter(month => month.index !== undefined).length <= PSR_TREND_DOTS_ONLY_MAX_MONTHS) return runs;
  let start = -1;
  months.forEach((month, i) => {
    const solid = month.index !== undefined && month.count > PSR_LOW_VOLUME_MONTH_MAX;
    if (solid && start < 0) start = i;
    if ((!solid || i === months.length - 1) && start >= 0) {
      const end = solid ? i : i - 1;
      if (end > start) runs.push([start, end]);
      start = -1;
    }
  });
  return runs;
}

/** Window label for the index: "12-month index" for a 365-day window. */
export function psrWindowIndexLabel(windowDays: number | undefined): string {
  if (windowDays === 365 || windowDays === 366) return '12-month index';
  return windowDays ? `${windowDays}-day index` : 'window index';
}

/** The low-volume note (SPEC §3.05); empty when no month is low volume. */
export function psrLowVolumeNote(months: readonly PsrTrendMonth[], index: number, windowDays: number | undefined): string {
  const withData = months.filter(month => month.index !== undefined);
  const low = withData.filter(month => month.count <= PSR_LOW_VOLUME_MONTH_MAX);
  const empty = months.length - withData.length;
  const parts: string[] = [];
  if (withData.length && low.length === withData.length) {
    parts.push(`Every month with statements has ${psrCountWord(PSR_LOW_VOLUME_MONTH_MAX)} or fewer, so each point is hollow and none are joined. Read the ${psrWindowIndexLabel(windowDays)} (${index}), not month-to-month movement.`);
  } else if (low.length) {
    parts.push(`${low.length} of ${withData.length} months with statements rest on ${psrCountWord(PSR_LOW_VOLUME_MONTH_MAX)} or fewer; those points are hollow and are not joined.`);
  }
  if (withData.length > 0 && withData.length <= PSR_TREND_DOTS_ONLY_MAX_MONTHS && low.length < withData.length) {
    parts.push(`Only ${psrCountWord(withData.length)} ${plural(withData.length, 'month carries', 'months carry')} dated local statements, so no line is drawn.`);
  }
  if (empty > 0) parts.push(`${empty} ${plural(empty, 'month has', 'months have')} no dated local statement; the ${plural(empty, 'gap is', 'gaps are')} left open, not filled.`);
  if (parts.length) parts.push('A month without a point may be a coverage gap, not a quiet month.');
  return parts.join(' ');
}

/* ── Change since the prior scan ─────────────────────────────────────── */

const signed = (n: number) => `${n > 0 ? '+' : n < 0 ? '−' : '±'}${Math.abs(n)}`;

/**
 * The change strip (assessed reports only). Numbers appear only when the
 * prior scan used the same query and its delta reconciles (the projection
 * strips them otherwise); a first scan implies no change.
 */
export function psrChangeModel(view: PsrReportView): { variant: 'comparable' | 'not-comparable' | 'first-scan'; items: PsrKpiItem[]; note: string } | undefined {
  if (view.index === undefined) return undefined;
  const prior = view.v2.priorScan;
  const now = Math.round(view.index);
  const nowLocal = psrStancedCount(view.site);
  if (!prior) {
    return {
      variant: 'first-scan',
      items: [{ key: 'Prior scans', value: '0' }, { key: 'Change', value: '—', note: 'first scan' }],
      note: 'First scan of this site. There is no earlier result to compare, and no change is implied.',
    };
  }
  const day = psrFormatDay(prior.fetchedAt) ?? prior.fetchedAt;
  if (prior.sameQuery && prior.supportIndex !== undefined && prior.supportIndexDelta !== undefined && prior.localObservationCount !== undefined) {
    const before = Math.round(prior.supportIndex);
    const bandBefore = psrBand(before);
    const bandNow = psrBand(now);
    const statements = (n: number) => `${n} local ${n === 1 ? 'statement' : 'statements'}`;
    return {
      variant: 'comparable',
      items: [
        { key: 'Compared with', value: day, unit: 'scan', note: 'same query fingerprint' },
        ...(prior.addedObservationCount !== undefined ? [{ key: 'New records', value: `+${prior.addedObservationCount}`, note: 'all scopes' }] : []),
        { key: 'Local support index', value: `${before} → ${now}`, note: bandBefore === bandNow ? `${PSR_BAND_LABELS[bandNow].word.toLowerCase()} band both times` : `${PSR_BAND_LABELS[bandBefore].word.toLowerCase()} → ${PSR_BAND_LABELS[bandNow].word.toLowerCase()} band` },
        { key: 'Change', value: signed(prior.supportIndexDelta), unit: 'index pts' },
      ],
      note: nowLocal === prior.localObservationCount
        ? `Same query fingerprint as the ${day} scan. The index rests on ${statements(nowLocal)} both times.`
        : `Same query fingerprint as the ${day} scan. The index now rests on ${statements(nowLocal)}, ${nowLocal > prior.localObservationCount ? 'up' : 'down'} from ${prior.localObservationCount}.`,
    };
  }
  return {
    variant: 'not-comparable',
    items: [
      { key: 'Compared with', value: day, unit: 'scan', note: prior.sameQuery ? 'same query fingerprint' : 'different query' },
      { key: 'Local support index', value: String(now), note: 'this scan' },
      { key: 'Change', value: '—', note: 'not comparable' },
    ],
    note: prior.sameQuery
      ? `The ${day} scan used the same query, but its local index does not reconcile with this scan, so the two indexes are not compared.`
      : `The ${day} scan ran a different query, or recorded no query fingerprint, so the two indexes are not compared.`,
  };
}

/* ── Research detail ──────────────────────────────────────────────────── */

export interface PsrSourceCounts { rows: number; siteRows: number; assessedLocal: number }
/** Per-source record counts from the projection (sums over technologies). */
export function psrSourceCounts(v2: PublicSentimentMetaV2, sourceId: string): PsrSourceCounts {
  const rows = (v2.countsByTechSource ?? []).filter(row => row.sourceId === sourceId);
  return {
    rows: rows.reduce((n, row) => n + row.rows, 0),
    siteRows: rows.reduce((n, row) => n + row.siteRows, 0),
    assessedLocal: rows.reduce((n, row) => n + row.assessedLocal, 0),
  };
}
export function psrFactCount(v2: PublicSentimentMetaV2, sourceId: string): number {
  return (v2.tier1Facts ?? []).filter(fact => fact.sourceId === sourceId).length;
}

export interface PsrFunnelModel {
  audit: PsrArchiveAudit;
  consistent: boolean;
  /** In-window documents not read (never negative). */
  unreadInWindow?: number;
  unreadAttachments?: number;
}
export function psrFunnel(audit: PsrArchiveAudit): PsrFunnelModel {
  const consistent = audit.funnelConsistent && audit.readInWindowDocuments !== undefined && audit.inWindowDocuments !== undefined;
  const unreadInWindow = consistent ? Math.max(0, audit.inWindowDocuments! - audit.readInWindowDocuments!) : undefined;
  const unreadAttachments = audit.listedAttachments !== undefined && audit.readAttachments !== undefined && audit.listedAttachments >= audit.readAttachments
    ? audit.listedAttachments - audit.readAttachments : undefined;
  return { audit, consistent, ...(unreadInWindow !== undefined ? { unreadInWindow } : {}), ...(unreadAttachments !== undefined ? { unreadAttachments } : {}) };
}
/** The partial-layer caveat under an archive read-through. */
export function psrArchiveCaveat(model: PsrFunnelModel): string {
  if (!model.consistent) return 'The archive counts do not nest, so unread documents cannot be counted; what was not read is unknown, not absent.';
  const parts: string[] = [];
  if (model.unreadInWindow) parts.push(`${model.unreadInWindow} in-window ${model.unreadInWindow === 1 ? 'document' : 'documents'}`);
  if (model.unreadAttachments) parts.push(`${model.unreadAttachments} listed ${model.unreadAttachments === 1 ? 'attachment' : 'attachments'}`);
  if (!parts.length) return 'Every in-window document and listed attachment was read.';
  const one = parts.length === 1 && (model.unreadInWindow ?? 0) + (model.unreadAttachments ?? 0) === 1;
  return `${parts.join(' and ')} ${one ? 'was' : 'were'} not read; ${one ? 'its contents are' : 'their contents are'} unknown.`;
}
export const psrPercent = (part: number, whole: number): string | undefined => (whole > 0 ? `${Math.round((part / whole) * 100)}%` : undefined);

/* ── Ledger ───────────────────────────────────────────────────────────── */

export type PsrLedgerGroupKey = 'local-stance' | 'local-unscored' | 'state' | 'country';
export interface PsrLedgerGroup { key: PsrLedgerGroupKey; rows: PsrLedgerRow[]; total: number }

/** SPEC order: local assessed, local unscored, statewide, national. */
export function psrLedgerGroups(view: Pick<PsrReportView, 'v2' | 'site' | 'stateScope' | 'country'>): PsrLedgerGroup[] {
  const rows = view.v2.ledger ?? [];
  const stanced = psrStancedCount(view.site);
  return [
    { key: 'local-stance', rows: rows.filter(row => row.scope === 'site' && row.status === 'local-stance'), total: stanced },
    { key: 'local-unscored', rows: rows.filter(row => row.scope === 'site' && row.status !== 'local-stance'), total: view.site?.unscored ?? 0 },
    { key: 'state', rows: rows.filter(row => row.scope === 'state'), total: view.stateScope?.total ?? 0 },
    { key: 'country', rows: rows.filter(row => row.scope === 'country'), total: view.country?.total ?? 0 },
  ];
}

/** 1-based index of the report reference with this exact URL; never the nearest. */
export function psrReferenceIndex(references: SiteRagReport['references'] | null | undefined, url: string | undefined): number | undefined {
  if (!url || !Array.isArray(references)) return undefined;
  const at = references.findIndex(ref => !!ref && typeof ref === 'object' && ref.url === url);
  return at >= 0 ? at + 1 : undefined;
}

/* ── Omissions ────────────────────────────────────────────────────────── */

const OMISSION_SCOPE: Readonly<Record<string, string>> = Object.freeze({
  stakeholderMap: 'contact roles and recorded positions.',
  perBtmSentiment: 'stance by technology, and which sources carried it.',
  themeDeepDives: 'the themes those statements raise.',
  trendAnalysis: 'the monthly local index and statement volume.',
  comparableSiteBenchmark: 'how local reception compares with peer sites.',
  mitigationPlaybook: 'next actions tied to recorded concerns.',
});
const PSR_BODY_SECTION_IDS = ['stakeholderMap', 'perBtmSentiment', 'themeDeepDives', 'trendAnalysis', 'comparableSiteBenchmark', 'mitigationPlaybook'] as const;

export interface PsrOmissionGroup {
  reason: 'insufficient-evidence' | 'source-unavailable';
  rows: Array<{ sectionId: string; description: string }>;
  /** The shared detail when every row carries the same text. */
  detail?: string;
  details: Array<{ sectionId: string; detail: string }>;
}

/**
 * The PSR chapters the reader will NOT see (recorded omissions that the
 * router hides), grouped by reason — the collapsed "Omitted · …" list.
 * Chapters retained with limits are not listed here.
 */
export function psrOmittedChapters(report: SiteRagReport): PsrOmissionGroup[] {
  const hidden = hiddenReportSectionIds(report);
  const records = report.qaFindings?.sectionOmissions ?? [];
  const groups: PsrOmissionGroup[] = [];
  for (const reason of ['insufficient-evidence', 'source-unavailable'] as const) {
    const rows = PSR_BODY_SECTION_IDS.flatMap(sectionId => {
      const record = records.find(row => row.sectionId === sectionId && row.reason === reason);
      return record && hidden.has(sectionId) ? [{ sectionId, detail: typeof record.detail === 'string' ? record.detail.trim() : '' }] : [];
    });
    if (!rows.length) continue;
    const lead = reason === 'insufficient-evidence' ? 'With dated local statements: ' : 'With usable sources: ';
    const details = rows.filter(row => row.detail).map(row => ({ sectionId: row.sectionId, detail: row.detail }));
    const shared = details.length === rows.length && new Set(details.map(row => row.detail)).size === 1 ? details[0]!.detail : undefined;
    groups.push({
      reason,
      rows: rows.map(row => ({ sectionId: row.sectionId, description: `${lead}${OMISSION_SCOPE[row.sectionId]}` })),
      ...(shared ? { detail: shared } : {}),
      details,
    });
  }
  return groups;
}

/** A chapter kept with limits (roles / next actions, never positions). */
export function psrRetainedWithLimits(report: SiteRagReport, sectionId: 'stakeholderMap' | 'mitigationPlaybook'): string | undefined {
  const record = report.qaFindings?.sectionOmissions?.find(row => row.sectionId === sectionId && row.reason === 'insufficient-evidence');
  if (!record || typeof record.detail !== 'string' || !record.detail.trim()) return undefined;
  if (hiddenReportSectionIds(report).has(sectionId)) return undefined;
  return record.detail.trim();
}
export const PSR_RETAINED_LABELS = Object.freeze({
  stakeholderMap: 'Retained with limits · roles, not positions',
  mitigationPlaybook: 'Retained with limits · next actions, not observed concerns',
});

/* ── Deterministic prose ──────────────────────────────────────────────── */

/**
 * Split template prose that is a sequence of `### Heading` blocks into
 * labelled parts (the deterministic builders write it that way). `undefined`
 * when the text is not entirely heading-led, so model prose is never split.
 */
export function psrHeadingSections(text: string): Array<{ heading: string; body: string }> | undefined {
  const trimmed = text.trim();
  if (!trimmed.startsWith('### ')) return undefined;
  const parts = trimmed.split(/^### /m).filter(part => part.trim().length > 0);
  const out = parts.map(part => {
    const newline = part.indexOf('\n');
    const heading = (newline < 0 ? part : part.slice(0, newline)).trim();
    const body = newline < 0 ? '' : part.slice(newline + 1).trim();
    return { heading, body };
  });
  return out.length > 0 && out.every(part => part.heading.length > 0 && part.heading.length <= 120 && part.body.length > 0) ? out : undefined;
}
